import { Skeleton } from "primereact/skeleton";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { QuotationCreateRequest } from "../../types/quotations/QuotationCreateRequest";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { instantQuotationSelector, postExternalInstantQuotationThunk, setExternalQuotationRequestState } from "../../features/instantQuotationSlice";
import { QuotationRequestState } from "../../types/quotations/QuotationRequestState";
import { useEffect, useRef } from "react";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { errorToast, successToast, warningToast } from "@idwal/idwal-react-components";
import { QUOTATIONS_ROUTE } from "../../config/router";
import { getUserInfoThunk, userSliceSelector } from "../../features/userSlice";
import { normaliseDate } from "../../utils/dates";

export const QuotationCreatePage = () => {
    const { user } = useAuthenticator((context) => [
        context.authStatus,
        context.user
    ]);
    
    const {t} = useTranslation("locale");
    const isFirstRender = useRef(true);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { UserInfoStatus } = useAppSelector(userSliceSelector);
    const { externalQuotationRequestState, quotationId } = useAppSelector(instantQuotationSelector);
    const shipImo = searchParams.get("ShipImo") || "";
    const inspectionTypeId = searchParams.get("InspectionTypeId") || "";
    const locationId = searchParams.get("LocationId") || "";
    const singleDate = searchParams.get("SingleDate") || "";
    const dateRange = searchParams.get("DateRange") || "";

    let dateFrom: string;
    let dateTo: string = "";
    if (dateRange) {
        const dates = dateRange.split(" - ");
        dateFrom = dates[0];
        dateTo = dates[1];
    } else {
        dateFrom = singleDate;
    }


    const parseDateString = (dateString: string) => {
        const [day, month, year] = dateString.split("/");
        return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    }

    const createQuotation = () => {
        const startDate = new Date(parseDateString(dateFrom));
        const endDate = dateTo ? new Date(parseDateString(dateTo)) : null;

        if (!shipImo || !inspectionTypeId || !locationId || !startDate) {
            warningToast(t("instantQuotation.instantQuotationSuccessFailure"));
            navigate(`/${QUOTATIONS_ROUTE}`);
            return;
        }

        const quotationCreateRequest = {
            shipImo: shipImo,
            inspectionTypeId: parseInt(inspectionTypeId),
            locationId: parseInt(locationId),
            startDate: normaliseDate(startDate),
            endDate: endDate ? normaliseDate(endDate) : null
        } as QuotationCreateRequest;
        dispatch(postExternalInstantQuotationThunk(quotationCreateRequest));
    }

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            createQuotation();
        }
    }, [searchParams]);

    useEffect(() => {
        if (user && UserInfoStatus === "idle") {
            dispatch(getUserInfoThunk({userId: user.username!}));
        }
    }, [user]);

    useEffect(() => {
        if (externalQuotationRequestState === QuotationRequestState.CompleteLead 
            || externalQuotationRequestState === QuotationRequestState.CompleteOffer 
            || externalQuotationRequestState === QuotationRequestState.CompleteDraft)
        {
            let message = (externalQuotationRequestState === QuotationRequestState.CompleteLead)
            ? t("instantQuotation.instantQuotationSuccessLead")
            : t("instantQuotation.instantQuotationSuccessDraft");
    
            successToast(message);
            dispatch(setExternalQuotationRequestState(QuotationRequestState.Idle));
            navigate(`/${QUOTATIONS_ROUTE}/` + quotationId);
        }
    
        if (externalQuotationRequestState === QuotationRequestState.Failed) {
            errorToast(t("instantQuotation.instantQuotationSuccessFailure"));
            dispatch(setExternalQuotationRequestState(QuotationRequestState.Idle));
            navigate(`/${QUOTATIONS_ROUTE}`);
        }
    }, [externalQuotationRequestState]);

    return (
        <Skeleton width="100%" height="70vh" />
    )
}